import { useState } from "react";
import { ChatFileMessage } from "context/adminContext";

import { ChatImageModal } from "./ChatImageModal";
import { FileTile, FileTileState } from "./FileTile";
import styles from "./index.module.css";
import { useChatFileDownload, useFreshFileUrls } from "./useChatFileDownload";
import { getThumbnailUrl, isImageFile } from "./utils";

type Props = {
  files: ChatFileMessage[];
  align?: "left" | "right";
};

const ChatImageTile = ({ file, onOpen }: { file: ChatFileMessage; onOpen: () => void }) => {
  const requestFreshUrls = useFreshFileUrls();
  const thumbnailUrl = getThumbnailUrl(file);
  // Состояние привязано к URL: свежая ссылка после истечения токена грузится заново
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const state: FileTileState =
    failedUrl === thumbnailUrl ? "unloaded-error" : loadedUrl === thumbnailUrl ? "loaded" : "loading";

  return (
    <FileTile
      key={attempt}
      kind="img"
      state={state}
      name={file.content}
      previewUrl={thumbnailUrl}
      onImageLoad={() => setLoadedUrl(thumbnailUrl)}
      onImageError={() => {
        setFailedUrl(thumbnailUrl);
        requestFreshUrls();
      }}
      onClick={() => {
        if (state === "loaded") onOpen();
        if (state === "unloaded-error") {
          setFailedUrl(null);
          setAttempt((prev) => prev + 1);
        }
      }}
    />
  );
};

const DOWNLOAD_TILE_STATE: Record<string, FileTileState> = {
  idle: "unloaded-error",
  error: "unloaded-error",
  loading: "loading",
  loaded: "loaded",
};

const ChatDocumentTile = ({ file }: { file: ChatFileMessage }) => {
  const { state, download, open } = useChatFileDownload(file);

  return (
    <FileTile
      kind="file"
      state={DOWNLOAD_TILE_STATE[state]}
      name={file.content}
      size={file.size}
      onClick={() => {
        if (state === "loaded") open();
        else if (state !== "loading") download();
      }}
    />
  );
};

// Картинки — строкой с переносом, документы — по одному в строке
export const ChatFilesMessage = ({ files, align = "left" }: Props) => {
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  const images = files.filter(isImageFile);
  const documents = files.filter((file) => !isImageFile(file));

  return (
    <div className={`${styles.filesMessage} ${align === "right" ? styles.alignRight : ""}`}>
      {images.length ? (
        <div className={styles.imagesRow}>
          {images.map((file, index) => (
            <ChatImageTile key={file.id} file={file} onOpen={() => setOpenedIndex(index)} />
          ))}
        </div>
      ) : null}
      {documents.map((file) => (
        <ChatDocumentTile key={file.id} file={file} />
      ))}
      <ChatImageModal
        images={images}
        index={openedIndex}
        onIndexChange={setOpenedIndex}
        onClose={() => setOpenedIndex(null)}
      />
    </div>
  );
};
